import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';
import React, { useContext } from 'react';

import { EntityDetailsFields } from 'common/components/entity-details';
import { getRiskOptions } from 'common/helpers/farm-helpers';
import { getMortgageStructureOptions } from 'common/helpers/farm-mortgage-helpers';
import { Locations } from 'common/components/farm/locations';
import { LossHistory } from 'common/components/farm/loss-history';
import FarmNavigator from 'core/components/farm-navigator';
import { PolicyWideEndorsements } from 'common/components/farm/policy-wide-endorsements';
import { LiabilityCoverages } from 'common/components/farm/liability-coverages';
import PolicyRestrictionsCard from 'common/components/farm/uw-questions-form/policy-restrictions-card';
import ChangeOfCoverageCard from 'common/components/farm/uw-questions-form/change-of-coverage-card';
import { AddMortgage, MortgageItem } from 'common/components/mortgage-details';
import AddApplicant from 'common/components/people/add-form';
import { useDisabledState } from 'common/disabled-context';
import { FarmOpenSectionsProvider } from 'common/hooks/farm-open-sections-context';
import { AuthContext } from 'core/components/auth';
import FormList from 'core/components/form-list';
import { useStore } from 'core/store';
import Applicant from '../applicant/applicant';
import FarmDiscounts from '../../discounts/farm-discounts';

const useStyles = makeStyles((theme) => ({
  innerContainer: {
    padding: theme.spacing(8),
    backgroundColor: theme.colors.card_background_primary_editable,
    borderRadius: theme.sizes.roundCorners
  }
}));

const FarmPolicy = () => {
  const session = useContext(AuthContext);
  const classes = useStyles();
  const {
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();

  const allowLicensedActions = policyStore.getAllowLicensedActions(session);
  const { disabled } = useDisabledState();
  const { values } = useFormikContext();

  // "Applies to structure" dropdown options for mortgages — only mortgageable structures (owned
  // dwellings and FO-6 barns/outbuildings), reusing the risk-option labels. (#32526)
  const policy = policyStore.policy;
  const riskOptions = getRiskOptions(values.farmLocations, policy?.policyType, policy?.state);
  const structureOptions = getMortgageStructureOptions(values.farmLocations, riskOptions);

  return (
    <FarmOpenSectionsProvider>
      <FarmNavigator
        isNavigatorOpen={policyStore.farmNavigatorOpen}
        closeNavigator={() => policyStore.setFarmNavigatorOpen(false)}
        location="policy"
      />
      <EntityDetailsFields />
      <FormList
        disabled={disabled || !allowLicensedActions}
        id="people"
        title="People"
        item={Applicant}
        addForm={AddApplicant}
        addLabel="Add People"
        fromPolicy
        hideLicenseFields
      />
      <Locations />
      <LiabilityCoverages />
      <FarmDiscounts disabled={disabled || !allowLicensedActions} />
      <PolicyWideEndorsements />
      {/* #32405: underwriters can add / change / remove the policy restriction on an issued policy
          here (self-gates on canApproveUWReview). Editing it and confirming rolls the FMI 122
          form and prints on the endorsement documents. */}
      <PolicyRestrictionsCard variant="policy" />
      {/* #34275: underwriters can add / change / remove the Change of Coverage note on an issued
          policy here (self-gates on canApproveUWReview). Editing it and confirming inserts the
          Change of Coverage notice into the endorsement packet. */}
      <ChangeOfCoverageCard variant="policy" />
      <FormList
        classes={classes}
        id="farm.mortgageDetails"
        item={MortgageItem}
        addForm={AddMortgage}
        title="Mortgage lender information"
        singular="Mortgage"
        plural="Mortgages"
        disabled={disabled}
        structureOptions={structureOptions}
      />
      {/* #33939 — Loss History Detail sits at the bottom of the farm policy details. Segment-level
          loss entries (pulled + manual) + a real-time Loss Summary. */}
      <LossHistory />
    </FarmOpenSectionsProvider>
  );
};

export default observer(FarmPolicy);
